import { navigateTo, useLinkResolver } from '#imports'
import { computed, ref } from 'vue'

import {
  BCP_47_LOCALES,
  type Bcp47Locale,
} from '@backmarket/http-api/src/standards/Locale'
import { useExperiments } from '@backmarket/nuxt-module-experiments/useExperiments'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useMarketplace } from '@backmarket/nuxt-module-marketplace/useMarketplace'
import { useLocalStorage } from '@backmarket/utils/composables/useLocalStorage'
import { toBcp47Locale } from '@backmarket/utils/string/toBcp47Locale'

import { HOME } from '~/scopes/home/route-names'
import { isEsUsLocaleSelectorEnabled } from '~/scopes/navigation/utils/isEsUsLocaleSelectorEnabled'

const BANNER_DISMISSED_KEY = 'country_switch_clicked_at'

function getBrowserLanguagePrefixes(): string[] {
  if (typeof navigator === 'undefined') return []

  let langs: readonly string[] = []

  if (navigator.languages?.length) {
    langs = navigator.languages
  } else if (navigator.language) {
    langs = [navigator.language]
  }

  return langs.map((l) => l.split('-')[0].toLowerCase())
}

export function useLocaleSwitchBanner() {
  const { market } = useMarketplace()
  const i18n = useI18n()
  const experiments = useExperiments()
  const localStorage = useLocalStorage()
  const { getLink } = useLinkResolver()
  const isBannerClosed = ref(false)

  const isExperimentEnabled = isEsUsLocaleSelectorEnabled(
    experiments['experiment.enableEsUsLocaleSelector'],
    market.countryCode,
  )

  const hasMultipleLocales = market.locales.length > 1

  const suggestedLocale = computed<Bcp47Locale | null>(() => {
    if (!isExperimentEnabled || !hasMultipleLocales) return null

    const { currentLocale } = i18n
    if (currentLocale !== BCP_47_LOCALES.en_US) return null

    const browserPrefixes = getBrowserLanguagePrefixes()

    for (const browserPrefix of browserPrefixes) {
      for (const locale of market.locales) {
        const bcp47 = toBcp47Locale(locale)
        const localePrefix = bcp47.split('-')[0]

        if (localePrefix === browserPrefix && bcp47 !== currentLocale) {
          return bcp47
        }
      }
    }

    return null
  })

  const linkToSuggestedLocale = computed<string | null>(() => {
    if (!suggestedLocale.value) return null

    return (
      getLink({
        link: { name: HOME },
        countryCode: market.countryCode,
        overrideLocale: suggestedLocale.value,
      })?.href ?? null
    )
  })

  const isLocaleSwitchSuggested = computed(() => {
    if (isBannerClosed.value) return false
    if (localStorage.hasKey(BANNER_DISMISSED_KEY)) return false

    return suggestedLocale.value !== null
  })

  const handleClose = () => {
    isBannerClosed.value = true
    localStorage.setItem(BANNER_DISMISSED_KEY, Date.now().toString())
  }

  const handleSwitch = () => {
    handleClose()
    void navigateTo(linkToSuggestedLocale.value, { external: true })
  }

  return {
    isLocaleSwitchSuggested,
    suggestedLocale,
    linkToSuggestedLocale,
    handleClose,
    handleSwitch,
  }
}
